import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Clock, SkipForward, Plus, List, PlayCircle } from "lucide-react";
import { VideoPlayer } from "@/components/VideoPlayer";

const beep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.value = 880; g.gain.value = 0.08;
    o.start();
    setTimeout(() => { o.stop(); ctx.close(); }, 300);
  } catch { /* audio optional */ }
};

export const WorkoutPlayer = ({ program, workout, exercisesById, onBack, onCompleted }) => {
  const exs = workout.exercises || [];
  const [sets, setSets] = useState(() =>
    exs.map((ex) => Array.from({ length: ex.sets || 3 }, () => ({ reps: "", weight: "", done: false })))
  );
  const [exIdx, setExIdx] = useState(0);
  const [setIdx, setSetIdx] = useState(0);
  const [restLeft, setRestLeft] = useState(0);
  const [resting, setResting] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [listView, setListView] = useState(false);
  const [startTime] = useState(Date.now());
  const [duration, setDuration] = useState("");
  const [rpe, setRpe] = useState(7);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastByExercise, setLastByExercise] = useState({});
  const restRef = useRef(null);

  useEffect(() => {
    api.get("/workouts/log").then((r) => {
      const map = {};
      for (const log of r.data) {
        for (const s of log.sets_completed || []) {
          if (s.exercise_id && !map[s.exercise_id]) map[s.exercise_id] = { date: log.date, sets: [] };
          if (s.exercise_id && map[s.exercise_id].date === log.date) map[s.exercise_id].sets.push(s);
        }
      }
      setLastByExercise(map);
    }).catch((err) => console.warn("Could not load workout history", err));
  }, []);

  useEffect(() => {
    if (!resting) return;
    restRef.current = setInterval(() => {
      setRestLeft((t) => {
        if (t <= 1) {
          clearInterval(restRef.current);
          setResting(false);
          beep();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(restRef.current);
  }, [resting]);

  const totalSets = useMemo(() => sets.reduce((n, rows) => n + rows.length, 0), [sets]);
  const doneSets = useMemo(() => sets.reduce((n, rows) => n + rows.filter((s) => s.done).length, 0), [sets]);

  const updateSet = (i, j, field, value) => {
    setSets((prev) => prev.map((rows, a) => (a === i ? rows.map((r, b) => (b === j ? { ...r, [field]: value } : r)) : rows)));
  };

  const advance = () => {
    if (setIdx + 1 < sets[exIdx].length) {
      setSetIdx(setIdx + 1);
      return { nextEx: exIdx, lastOne: false };
    }
    if (exIdx + 1 < exs.length) {
      setExIdx(exIdx + 1);
      setSetIdx(0);
      return { nextEx: exIdx + 1, lastOne: false };
    }
    return { nextEx: exIdx, lastOne: true };
  };

  const logSet = () => {
    const ex = exs[exIdx];
    setSets((prev) => prev.map((rows, a) => (a === exIdx
      ? rows.map((r, b) => (b === setIdx ? { ...r, reps: r.reps || String(ex.reps || ""), done: true } : r))
      : rows)));
    const { lastOne } = advance();
    if (lastOne) {
      setDuration(String(Math.max(1, Math.round((Date.now() - startTime) / 60000))));
      setFinishing(true);
      toast.success("All sets done — log it!");
    } else {
      const rest = parseInt(ex.rest_seconds, 10) || 75;
      setRestLeft(rest);
      setResting(true);
    }
  };

  const skipExercise = () => {
    if (exIdx + 1 < exs.length) {
      setExIdx(exIdx + 1);
      setSetIdx(0);
    } else {
      setDuration(String(Math.max(1, Math.round((Date.now() - startTime) / 60000))));
      setFinishing(true);
    }
  };

  const complete = async () => {
    setBusy(true);
    try {
      const sets_completed = [];
      exs.forEach((ex, i) => {
        sets[i].forEach((s, j) => {
          if (s.reps || s.weight) {
            sets_completed.push({
              exercise_id: ex.exercise_id, exercise_name: ex.name,
              set_number: j + 1, reps: s.reps, weight: s.weight,
            });
          }
        });
      });
      await api.post("/workouts/log", {
        program_id: program.program_id, week: workout.week, day: workout.day,
        workout_name: workout.name, date: new Date().toISOString().slice(0, 10),
        duration_minutes: duration ? parseInt(duration, 10) : null,
        rpe, completed: true, sets_completed, notes: notes || null,
      });
      toast.success("Workout logged — beautiful work!");
      onCompleted();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const ex = exs[exIdx];
  const lib = ex ? exercisesById[ex.exercise_id] : null;
  const last = ex ? lastByExercise[ex.exercise_id] : null;
  const nextUp = setIdx + 1 < (sets[exIdx]?.length || 0)
    ? `${ex?.name} — set ${setIdx + 1} of ${sets[exIdx]?.length}`
    : exs[exIdx + 1] ? `Next: ${exs[exIdx + 1].name}` : "Last set!";

  return (
    <div className="space-y-8 lg:mt-0 mt-10" data-testid="workout-player">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button className="btn-ghost" onClick={onBack} data-testid="player-back"><ArrowLeft size={14} /> Exit</button>
        {!finishing && (
          <button className="btn-secondary text-xs" onClick={() => setListView(!listView)} data-testid="list-view-toggle">
            <List size={12} /> {listView ? "Guided mode" : "List view"}
          </button>
        )}
      </div>

      <header>
        <div className="overline">Week {workout.week} · Day {workout.day}</div>
        <h1 className="font-serif text-4xl sm:text-5xl mt-3">{workout.name}</h1>
        <div className="mt-5 h-1 w-full" style={{ background: "var(--surface-2)" }} data-testid="progress-bar">
          <div className="h-1 transition-all duration-500" style={{ width: `${totalSets ? (doneSets / totalSets) * 100 : 0}%`, background: "var(--accent)" }} />
        </div>
        <div className="overline mt-2">{doneSets} / {totalSets} sets</div>
      </header>

      {resting && !finishing && !listView && (
        <div className="card-luxe text-center py-14" data-testid="rest-overlay">
          <div className="overline">Rest</div>
          <div className="font-mono-luxe text-7xl mt-4" data-testid="rest-countdown">
            {Math.floor(restLeft / 60)}:{String(restLeft % 60).padStart(2, "0")}
          </div>
          <p className="text-sm text-secondary-luxe mt-3">{nextUp}</p>
          <div className="flex gap-3 justify-center mt-7">
            <button className="btn-secondary text-xs" onClick={() => setRestLeft((t) => t + 30)} data-testid="add-rest-btn"><Plus size={12} /> 30s</button>
            <button className="btn-primary text-xs" onClick={() => { clearInterval(restRef.current); setResting(false); setRestLeft(0); }} data-testid="skip-rest-btn">
              <SkipForward size={12} /> Skip rest
            </button>
          </div>
        </div>
      )}

      {!resting && !finishing && !listView && ex && (
        <div className="card-luxe" data-testid="guided-view">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="overline">Exercise {exIdx + 1} of {exs.length}</div>
              <h2 className="font-serif text-3xl mt-2" data-testid="guided-exercise-name">{ex.name}</h2>
              <p className="text-xs text-secondary-luxe mt-1">
                Target: {ex.sets} × {ex.reps} reps {ex.rest_seconds ? `· ${ex.rest_seconds}s rest` : "· 75s rest"}
              </p>
              {ex.notes && <p className="text-xs mt-1" style={{ color: "var(--accent)" }}>{ex.notes}</p>}
            </div>
            {last && (
              <div className="text-right text-xs text-tertiary-luxe">
                <div className="overline" style={{ fontSize: "9px" }}>Last time · {last.date}</div>
                {last.sets.slice(0, 3).map((s, j) => (
                  <div key={`last-${s.set_number ?? j}`}>{s.reps || "—"} reps {s.weight ? `@ ${s.weight}` : ""}</div>
                ))}
              </div>
            )}
          </div>

          {lib?.video_url ? (
            <div className="mt-5 max-w-xl"><VideoPlayer url={lib.video_url} /></div>
          ) : (
            <p className="text-xs text-tertiary-luxe mt-4 flex items-center gap-2"><PlayCircle size={12} /> No demo video for this one yet.</p>
          )}

          <div className="mt-7 p-5" style={{ background: "var(--surface-2)" }}>
            <div className="overline" data-testid="guided-set-counter">Set {setIdx + 1} of {sets[exIdx].length}</div>
            <div className="flex items-end gap-4 flex-wrap mt-4">
              <div>
                <label className="label-luxe">Reps</label>
                <input className="input-luxe w-28 text-lg" inputMode="numeric" placeholder={String(ex.reps || "")}
                  value={sets[exIdx][setIdx].reps} onChange={(e) => updateSet(exIdx, setIdx, "reps", e.target.value)}
                  data-testid="guided-reps-input" />
              </div>
              <div>
                <label className="label-luxe">Weight (lb)</label>
                <input className="input-luxe w-32 text-lg" inputMode="decimal" placeholder="—"
                  value={sets[exIdx][setIdx].weight} onChange={(e) => updateSet(exIdx, setIdx, "weight", e.target.value)}
                  data-testid="guided-weight-input" />
              </div>
              <button className="btn-primary" onClick={logSet} data-testid="log-set-btn">
                <CheckCircle2 size={14} /> Log set {setIdx + 1}
              </button>
            </div>
          </div>

          <button className="btn-ghost text-xs mt-4" onClick={skipExercise} data-testid="skip-exercise-btn">
            <SkipForward size={12} /> Skip this exercise
          </button>
        </div>
      )}

      {(listView || finishing) && (
        <div className="space-y-6" data-testid="finish-review">
          {exs.map((e2, i) => {
            const lib2 = exercisesById[e2.exercise_id];
            return (
              <div key={`${e2.exercise_id || e2.name}-${i}`} className="card-luxe" data-testid={`player-exercise-${i}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-serif text-2xl">{e2.name}</h3>
                  <span className="text-xs text-secondary-luxe">Target: {e2.sets} × {e2.reps}</span>
                </div>
                {!finishing && lib2?.video_url && <div className="mt-4 max-w-xl"><VideoPlayer url={lib2.video_url} /></div>}
                <div className="mt-4 space-y-2">
                  {sets[i].map((s, j) => (
                    <div key={`${e2.exercise_id || e2.name}-set-${j}`} className="flex items-center gap-3">
                      <span className="font-label text-[10px] tracking-[0.2em] uppercase text-tertiary-luxe w-12">Set {j + 1}</span>
                      <input className="input-luxe w-28" inputMode="numeric" placeholder={`${e2.reps} reps`} value={s.reps}
                        onChange={(e) => updateSet(i, j, "reps", e.target.value)} data-testid={`set-reps-${i}-${j}`} />
                      <input className="input-luxe w-32" inputMode="decimal" placeholder="Weight (lb)" value={s.weight}
                        onChange={(e) => updateSet(i, j, "weight", e.target.value)} data-testid={`set-weight-${i}-${j}`} />
                      {s.done && <CheckCircle2 size={14} style={{ color: "var(--accent)" }} />}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(finishing || listView) && (
        <div className="card-luxe space-y-5" data-testid="complete-panel">
          <div className="overline">Finish &amp; log</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="label-luxe flex items-center gap-2"><Clock size={12} /> Duration (minutes)</label>
              <input type="number" min="1" className="input-luxe" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="duration-input" />
            </div>
            <div>
              <label className="label-luxe">Effort (RPE 1–10)</label>
              <div className="flex gap-1 flex-wrap mt-1">
                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                  <button key={n} type="button" onClick={() => setRpe(n)} data-testid={`rpe-${n}`}
                    className="w-9 h-9 font-label text-xs transition-colors"
                    style={{
                      background: rpe === n ? "var(--accent)" : "var(--surface-2)",
                      color: rpe === n ? "var(--bg)" : "var(--text-secondary)",
                      border: "1px solid var(--border)",
                    }}>{n}</button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <label className="label-luxe">Notes for your coach</label>
            <textarea className="input-luxe" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="workout-notes" />
          </div>
          <button className="btn-primary w-full justify-center" onClick={complete} disabled={busy} data-testid="complete-workout-btn">
            <CheckCircle2 size={14} /> {busy ? "Logging…" : "Complete workout"}
          </button>
        </div>
      )}
    </div>
  );
};
